import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Informar baja de inventario',
    seo_description: '',
    seo_keywords: '',
    page_id: PagesId.WriteOffInventory,
    breadcrumbs: {
      home: 'Inicio',
      items_home: 'Productos e inventario',
      write_off_inventory: 'Informar baja de inventario',
    },
    article: {
      title: 'Informar baja de inventario',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar <em>inventario</em> y registrar <em>bajas de stock</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST}>Asistente de wicadu</a>.`,
        `<strong>Vendedores</strong> o <strong>Abastecedor</strong> con permisos para para <em>reportar bajas de inventario</em> y <em>registrar motivos de ajuste</em>.`,
      ],
      about: {
        title: 'Acerca de baja de inventario',
        content_type: ContentType.Paragraph,
        content: [
          'Una <strong>baja de inventario</strong> es el proceso de eliminar productos del registro de inventario cuando estos esten <em>dañados</em>, <em>perdidos</em>, <em>robados</em>, <em>vencidos</em> o ya no sean vendibles a su valor original.',
          'En <strong>wicadu</strong>, informar una baja de inventario te permite mantener el stock actualizado, evitar errores en pedidos y generar reportes financieros más precisos.',
          'En este artículo aprenderás cómo registrar una baja paso a paso, cuáles son las causas más comunes y cómo solucionar posibles problemas durante el proceso.'
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Asegúrate de tener lo siguiente preparado:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Identifica los productos a ajustar, incluyendo <em>SKU</em> (Opcional), <em>monto</em>, <em>nombre del producto y cantidad</em>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Motivo de la baja (por ejemplo, <em>productos dañados</em>, <em>vencidos</em> o <em>perdidos</em>) para categorizar el ajuste. Consulta la sección "<strong>Causas de la pérdida de inventario</strong>"`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Asegúrate de que la cantidad a dar de baja esté actualmente registrada en el sistema, evitando errores por stock negativo.'
                ]
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Si cometes un error al registrar una baja, puedes anularla desde el historial de inventario, siempre que tengas los permisos necesarios'
                ]
              },

            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Estos pasos previos te ayudarán a realizar un registro más rápido, preciso y trazable dentro de tu inventario.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            class: 'msg-alert',
            content: [
              '<strong>Importante</strong>: Los planes gratuitos no incluyen la funcionalidad de <strong>informar bajas de inventario</strong>. Verifica o actualiza tu plan en Configuración > Planes y facturación',
            ],
          },

        ],
      },
      common_causes: {
        title: 'Causas de la pérdida de inventario',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'La pérdida de inventario puede deberse a muchos factores que suelen actuar combinados entre sí. Estas son algunas de las causas más comunes de las pérdidas:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Producto dañado</strong>: Producto con golpes, roturas o defectos que impiden su venta o uso adecuado, incumpliendo los estándares mínimos de calidad.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Deterioro</strong>: El producto ha perdido calidad por factores como humedad, luz o almacenamiento inadecuado, afectando su estado para la venta.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Vencimiento</strong>: Producto que superó la fecha de caducidad o uso recomendado, por lo que su venta está prohibida por normativas sanitarias.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Producto perdido</strong>: Producto que no se encuentra físicamente en inventario, posiblemente por errores de conteo, extravío o movimientos no registrados.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Hurto o robo</strong>: El producto fue sustraído sin autorización por terceros o personal interno.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Venta prohibida por ley</strong>: Producto que ya no puede venderse debido a cambios legales o restricciones específicas del país.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Ajuste administrativo</strong>: Corrección de errores en el registro, como duplicaciones o diferencias detectadas en auditorías, sin pérdida física real.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Errores de producción</strong>: Producto con fallas graves desde su fabricación que impiden su venta, aunque no presente daño visible.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Obsolescencia</strong>: Producto fuera de mercado por falta de demanda o reemplazo tecnológico, sin valor comercial y ocupando espacio en inventario.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Uso interno</strong>: Producto utilizado dentro de la empresa para pruebas, demostraciones o consumo del equipo, registrando su salida del inventario.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Producto abierto o manipulado</strong>: Producto con empaque abierto o alterado, afectando su presentación o seguridad, y no apto para la venta.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Error de proveedor</strong>: Producto recibido con defectos o especificaciones incorrectas, que no se incorporó al inventario utilizable.',
                ],
              }
            ]
          },
        ],

      },
      how_to_add: {
        title: 'Pasos para informar baja de inventario',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Accede a la funcionalidad de <em>Inventario</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente wicadu">Asistente de wicadu</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona un producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Usa el buscador o navega por la lista para encontrar el producto que deseas ajustar. Haz clic en su nombre para abrir sus detalles'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Iniciar el proceso de baja de inventario:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En la sección <em>General</em>, haz clic en <strong>Informar</strong> para registrar la baja de inventario.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ingresa los detalles: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Selecciona el motivo de la baja, indica la cantidad de unidades a descontar y el monto del ajuste.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Confirma el registro: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en "<b>Informar</b>" para guardar la baja. Esto actualizará el inventario y creará un registro visible en la misma sección <strong>Baja de inventario</strong>, como parte del historial del producto.'
                ],
              },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'Pasos para anular una baja de inventario',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Accede a la funcionalidad de <em>Inventario</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente wicadu">Asistente de wicadu</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona un producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Usa el buscador o navega por la lista para encontrar el producto que deseas ajustar. Haz clic en su nombre para abrir sus detalles'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona el registro:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En la sección <em>General</em>, selecciona el registro de <em>baja de inventario</em> que deseas anular.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Anula la baja de inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En los detalles del informe, haz clic en el botón <b>Anular</b>. Al confirmar, el sistema restaurará automáticamente las unidades al inventario.'
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Una vez que la baja de inventario ha sido registrada o anulada, considera lo siguiente:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Notificar a tu equipo: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Si otros miembros del equipo gestionan inventario, comunícales el cambio para mantener una coordinación adecuada.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verificar el stock actualizado: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirma que el nuevo stock se refleje correctamente en el <strong>Asistente de wicadu</strong> y en tu <strong>tienda en línea</strong>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Actualizar reportes y registros contables: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Si usas hojas de cálculo, un ERP o tienes integración contable con <strong>wicadu</strong>, asegúrate de sincronizar el ajuste para reflejar el nuevo valor de inventario en todos tus sistemas.'
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No veo el botón "Informar"</strong>: Verifica que tengas un plan activo que incluya esta función. Los planes gratuitos no permiten informar bajas de inventario.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El stock no se actualizó después de anular</strong>: Puede tratarse de un error de sincronización temporal. Vuelve a revisar en unos minutos o actualiza manualmente si usas sistemas externos.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El motivo de baja no aparece</strong>: Los motivos disponibles son fijos. Si necesitas registrar un caso especial, selecciona “Otro” y agrega una nota en los comentarios.',
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'Inventory Write-Offs',
    seo_description: '',
    seo_keywords: '',
    page_id: PagesId.WriteOffInventory,
    breadcrumbs: {
      home: 'Home',
      items_home: 'Products and inventory',
      write_off_inventory: 'Inventory Write-Offs',
    },
    article: {
      title: 'Inventory Write-Offs',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage <em>inventory</em> and record <em>stock reductions</em> within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assitant">wicadu Assistant</a>.`,
        `<strong>Sellers</strong> or <strong>Suppliers</strong> with permissions to <em>report inventory reductions</em> and <em>record adjustment reasons</em>.`,
      ],
      about: {
        title: 'About Inventory Write-Offs',
        content_type: ContentType.Paragraph,
        content: [
          'An <strong>inventory write-off</strong> is the process of <em>removing products</em> from your stock records when items are <em>damaged</em>, <em>lost</em>, <em>stolen</em>, <em>expired</em>, or no longer sellable at their original value.',
          'In <strong>wicadu</strong>, reporting an <strong>inventory write-off</strong> helps keep your <em>stock levels accurate</em>, <em>prevents order errors</em>, and ensures your financial reports reflect the real value of your inventory.',
          'This guide will walk you through how to register a write-off step by step, explain the most common reasons for doing so, and help you resolve any issues that may arise during the process.'
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Make sure you have the following ready:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Identify the products to be adjusted, including <em>SKU</em> (optional), <em>product name</em>, <em>quantity</em>, and <em>estimated value</em>.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Determine the reason for the write-off (e.g., damaged, expired, or lost items) to properly categorize the adjustment. See the section "<strong>Common reasons for Inventory Write-Offs</strong>"'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Ensure that the quantity to be written off is currently available in your system to avoid negative stock errors.'
                ]
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'If you make a mistake when registering a write-off, you can reverse it from the inventory history—if your role has the necessary permissions.'
                ]
              },

            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Preparing this information in advance will help you complete the write-off process faster, more accurately, and with full traceability.'
            ],
          },
          {
            content_type: ContentType.Paragraph,
            class: 'msg-alert',
            content: [
              '<strong>Important</strong>: Free plans do not include the <strong>inventory write-off</strong> feature. Please check or upgrade your plan under Settings > Plans & Billing.',
            ],
          },
        ],
      },
      common_causes: {
        title: 'Common reasons for Inventory Write-Offs',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Inventory loss can result from various factors, often acting together. Here are some of the most frequent causes:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Damaged Product</strong>: Items with dents, breaks, or defects that prevent proper sale or use, failing to meet minimum quality standards.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Deterioration</strong>: Products that have lost quality due to humidity, light exposure, or improper storage, affecting their saleability.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Expiration</strong>: Products past their expiry or recommended use date, prohibited from sale by health regulations.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Lost Product</strong>: Items physically missing from inventory due to counting errors, misplacement, or unrecorded movements.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Theft or Robbery</strong>: Products taken without authorization by third parties or internal personnel.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Prohibited Sale by Law</strong>: Items no longer allowed for sale due to legal changes or country-specific restrictions.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Administrative Adjustment</strong>: Corrections of record errors such as duplicates or discrepancies found during audits, without actual physical loss.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Production Errors</strong>: Products with serious manufacturing defects that prevent sale, even if no visible damage is present.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Obsolescence</strong>: Products out of market due to low demand or technological replacement, with no commercial value but occupying inventory space.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Internal Use</strong>: Products used internally for testing, demonstrations, or staff consumption, recorded as inventory outflow.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Opened or Tampered Product</strong>: Items with opened or altered packaging that compromise presentation or safety, making them unsellable.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Supplier Error</strong>: Products received with defects or incorrect specifications, excluded from usable inventory.',
                ],
              },
            ]
          },
        ],

      },
      how_to_add: {
        title: 'Steps to Report an Inventory Write-Off',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to the Inventory module:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Inventory</em> section within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select a product: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Use the search bar or browse the product list to locate the item you want to adjust. Click on its name to open the product details.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Start the write-off process:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'In the <em>General</em> section, click on Inform to initiate the <strong>inventory write-off</strong>.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Enter the details: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Choose the reason for the adjustment, specify the quantity to deduct, and enter the value of the write-off.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Confirm and submit: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click <b>Inform</b> to finalize the process. This will update your inventory and create a record under the <strong>Inventory Write-Off</strong> section, visible as part of the product history.'
                ],
              },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'How to Cancel an Inventory Write-Off',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to the Inventory module:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Inventory</em> section within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select a product: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Use the search bar or browse the list to find the product you want to update. Click the product name to open its detail view.'
                ],
              },
            ],
          },

          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Locate the write-off record:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Within the <em>General</em> section, find and select the <em>write-off</em> entry you wish to cancel.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Cancel the write-off:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'In the record details, click the <b>Cancel</b> button. Once confirmed, the system will automatically restore the removed units back into your inventory.'
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Once the <em>inventory write-off</em> has been recorded or canceled, consider the following actions to ensure consistency across your operations:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Notify your team: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'If other team members handle inventory tasks, inform them about the update to maintain proper coordination and avoid duplicate actions.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verify the updated stock: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Double-check that the adjusted stock is correctly reflected in both the <strong>wicadu Assistant</strong> and your <strong>online store</strong>.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Update reports and accounting records: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'If you use spreadsheets, ERP systems, or have an accounting integration with <strong>wicadu</strong>, make sure to synchronize the change to reflect the updated inventory value across all platforms.'
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Missing "Report" Button</strong>: Verify that you have an active plan that includes this feature. Free plans do not support inventory write-offs.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Stock not updated after cancellation</strong>: This may be due to a temporary synchronization error. Wait a few minutes and check again, or update manually if you use external systems.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Unavailable Write-Off Reason</strong>: The available reasons are fixed. If you need to record a special case, select "Other" and add a note in the comments.',
            ],
          },
        ],
      },
    },
  },
  fr: {
    seo_title: "Enregistrer une sortie d'inventaire",
    seo_description:
      "Découvrez comment déclarer une perte, un dommage ou un ajustement négatif de stock dans wicadu afin de garder votre inventaire exact.",
    seo_keywords:
      "sortie d'inventaire, ajustement stock, perte stock, dommage stock, inventaire wicadu",
    page_id: PagesId.WriteOffInventory,
    breadcrumbs: {
      home: 'Accueil',
      items_home: 'Produits et inventaire',
      write_off_inventory: "Enregistrer une sortie d'inventaire",
    },
    article: {
      title: "Enregistrer une sortie d'inventaire",
      intended: [
        `<strong>Propriétaires</strong>, <strong>administrateurs</strong> et équipes responsables du <em>stock</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        `<strong>Vendeurs</strong> ou responsables d’entrepôt autorisés à <em>corriger les quantités disponibles</em>.`,
      ],
      about: {
        title: 'À propos des sorties de stock',
        content_type: ContentType.Paragraph,
        content: [
          "Une <strong>sortie d'inventaire</strong> sert à enregistrer une diminution de stock qui ne provient pas d'une vente normale, comme une perte, un dommage, un produit invendable ou une correction d’écart. Dans <strong>wicadu</strong>, cette opération permet de garder un <strong>inventaire</strong> plus fiable et plus proche de la réalité.",
          'Enregistrer correctement ces mouvements vous aide à mieux comprendre les écarts, à renforcer votre contrôle sur le <strong>stock</strong> et à éviter que votre catalogue affiche des quantités incorrectes. C’est aussi une bonne pratique pour améliorer la qualité de votre suivi opérationnel.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Assurez-vous d’avoir préparé les éléments suivants :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              { content_type: ContentType.Paragraph, content: ['Identifiez les produits à ajuster, y compris le <em>SKU</em> (facultatif), le <em>nom du produit</em>, la <em>quantité</em> et la <em>valeur estimée</em>.'] },
              { content_type: ContentType.Paragraph, content: ['Déterminez le motif de la sortie de stock (par exemple, produits endommagés, expirés ou perdus) afin de catégoriser correctement l’ajustement. Consultez la section "<strong>Causes courantes des sorties d’inventaire</strong>".'] },
              { content_type: ContentType.Paragraph, content: ['Assurez-vous que la quantité à sortir est actuellement disponible dans votre système afin d’éviter les erreurs de stock négatif.'] },
              { content_type: ContentType.Paragraph, content: ['Si vous faites une erreur lors de l’enregistrement d’une sortie, vous pouvez l’annuler depuis l’historique d’inventaire, si votre rôle dispose des autorisations nécessaires.'] },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Préparer ces informations à l’avance vous aidera à effectuer la sortie de stock plus rapidement, avec plus de précision et une meilleure traçabilité.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            class: 'msg-alert',
            content: [
              '<strong>Important</strong> : les forfaits gratuits n’incluent pas la fonctionnalité de <strong>sortie d’inventaire</strong>. Vérifiez ou mettez à niveau votre forfait dans Paramètres > Forfaits et facturation.',
            ],
          },
        ],
      },
      common_causes: {
        title: 'Causes courantes des sorties d’inventaire',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'La perte d’inventaire peut provenir de plusieurs facteurs, souvent combinés entre eux. Voici quelques-unes des causes les plus fréquentes :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              { content_type: ContentType.Paragraph, content: ['<strong>Produit endommagé</strong> : articles présentant des chocs, cassures ou défauts qui empêchent une vente ou une utilisation correcte, et qui ne respectent plus les standards minimums de qualité.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Détérioration</strong> : produits ayant perdu en qualité à cause de l’humidité, de la lumière ou d’un mauvais stockage, ce qui affecte leur aptitude à la vente.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Expiration</strong> : produits ayant dépassé leur date de péremption ou leur durée d’utilisation recommandée, dont la vente est interdite par les réglementations sanitaires.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Produit perdu</strong> : articles physiquement absents de l’inventaire à cause d’erreurs de comptage, d’un égarement ou de mouvements non enregistrés.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Vol ou larcin</strong> : produits retirés sans autorisation par des tiers ou par du personnel interne.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Vente interdite par la loi</strong> : articles qui ne peuvent plus être vendus en raison de changements légaux ou de restrictions spécifiques à un pays.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Ajustement administratif</strong> : correction d’erreurs d’enregistrement, comme des doublons ou des écarts détectés lors d’audits, sans perte physique réelle.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Erreurs de production</strong> : produits présentant de graves défauts de fabrication empêchant leur vente, même si aucun dommage visible n’est apparent.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Obsolescence</strong> : produits sortis du marché en raison d’une faible demande ou d’un remplacement technologique, sans valeur commerciale mais occupant encore de l’espace en stock.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Usage interne</strong> : produits utilisés en interne pour des tests, des démonstrations ou la consommation de l’équipe, enregistrés comme une sortie du stock.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Produit ouvert ou altéré</strong> : articles dont l’emballage a été ouvert ou modifié, compromettant leur présentation ou leur sécurité, et les rendant invendables.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Erreur fournisseur</strong> : produits reçus avec des défauts ou des spécifications incorrectes, exclus du stock utilisable.'] },
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Étapes pour enregistrer une sortie d’inventaire',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Aller au module Inventaire : '] },
              { content_type: ContentType.Paragraph, content: [`Accédez à la fonctionnalité <em>Inventaire</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST} title="Assistant wicadu">assistant wicadu</a>.`] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner un produit : '] },
              { content_type: ContentType.Paragraph, content: ['Utilisez la barre de recherche ou parcourez la liste des produits pour localiser l’article que vous souhaitez ajuster. Cliquez sur son nom pour ouvrir les détails du produit.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Commencer le processus de sortie d’inventaire :'] },
              { content_type: ContentType.Paragraph, content: ['Dans la section <em>Général</em>, cliquez sur <strong>Informer</strong> pour lancer la <strong>sortie d’inventaire</strong>.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Saisir les détails : '] },
              { content_type: ContentType.Paragraph, content: ['Choisissez le motif de l’ajustement, indiquez la quantité à déduire et saisissez la valeur de la sortie de stock.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer et enregistrer : '] },
              { content_type: ContentType.Paragraph, content: ['Cliquez sur <b>Informer</b> pour finaliser le processus. Cela mettra à jour votre inventaire et créera un enregistrement dans la section <strong>Sortie d’inventaire</strong>, visible dans l’historique du produit.'] },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'Comment annuler une sortie d’inventaire',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Aller au module Inventaire : '] },
              { content_type: ContentType.Paragraph, content: [`Accédez à la fonctionnalité <em>Inventaire</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST} title="Assistant wicadu">assistant wicadu</a>.`] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner un produit : '] },
              { content_type: ContentType.Paragraph, content: ['Utilisez la barre de recherche ou parcourez la liste pour trouver le produit que vous souhaitez mettre à jour. Cliquez sur le nom du produit pour ouvrir sa vue détaillée.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Localiser l’enregistrement de sortie :'] },
              { content_type: ContentType.Paragraph, content: ['Dans la section <em>Général</em>, trouvez et sélectionnez l’entrée de <em>sortie d’inventaire</em> que vous souhaitez annuler.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Annuler la sortie :'] },
              { content_type: ContentType.Paragraph, content: ['Dans les détails de l’enregistrement, cliquez sur le bouton <b>Annuler</b>. Une fois confirmé, le système restaurera automatiquement les unités retirées dans votre inventaire.'] },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Une fois la <em>sortie d’inventaire</em> enregistrée ou annulée, envisagez les actions suivantes afin de garantir la cohérence de vos opérations :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Informer votre équipe : '] },
                  { content_type: ContentType.Paragraph, content: ['Si d’autres membres de l’équipe gèrent des tâches liées à l’inventaire, informez-les de la mise à jour afin de maintenir une bonne coordination et d’éviter les actions en double.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier le stock mis à jour : '] },
                  { content_type: ContentType.Paragraph, content: ['Contrôlez que le stock ajusté est correctement reflété à la fois dans <strong>l’assistant wicadu</strong> et dans votre <strong>boutique en ligne</strong>.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Mettre à jour les rapports et les registres comptables : '] },
                  { content_type: ContentType.Paragraph, content: ['Si vous utilisez des feuilles de calcul, un ERP ou une intégration comptable avec <strong>wicadu</strong>, veillez à synchroniser le changement afin que la nouvelle valeur d’inventaire soit correctement reflétée sur toutes les plateformes.'] },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Le bouton "Informer" n’apparaît pas</strong> : vérifiez que vous disposez d’un forfait actif incluant cette fonctionnalité. Les forfaits gratuits ne prennent pas en charge les sorties d’inventaire.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le stock n’a pas été mis à jour après l’annulation</strong> : cela peut être dû à une erreur de synchronisation temporaire. Attendez quelques minutes puis vérifiez de nouveau, ou mettez à jour manuellement si vous utilisez des systèmes externes.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le motif de sortie d’inventaire n’est pas disponible</strong> : les motifs disponibles sont fixes. Si vous devez enregistrer un cas particulier, sélectionnez "Autre" et ajoutez une note dans les commentaires.'] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
